/**
 * Maps raw error messages to user-friendly messages
 * Categorizes errors as customer errors vs system errors
 */

// Customer error patterns - errors that are the customer's responsibility
const CUSTOMER_ERROR_PATTERNS = [
    { pattern: /insufficient funds?/i, message: 'Transaction declined due to insufficient funds' },
    { pattern: /not sufficient funds?/i, message: 'Transaction declined due to insufficient funds' },

    { pattern: /card declined/i, message: 'Transaction declined by your bank' },
    { pattern: /invalid card/i, message: 'Invalid card details provided' },
    { pattern: /expired card/i, message: 'Card has expired' },
    { pattern: /incorrect pin/i, message: 'Incorrect PIN entered' },
    { pattern: /card blocked/i, message: 'Card has been blocked by your bank' },
    { pattern: /limit exceeded/i, message: 'Transaction amount exceeds your card limit' },
    { pattern: /authentication failed/i, message: 'Card authentication failed' },
    { pattern: /invalid cvv/i, message: 'Invalid CVV code provided' },
    { pattern: /invalid expiry/i, message: 'Invalid card expiry date' },
    { pattern: /card not supported/i, message: 'This card type is not supported' },
    { pattern: /transaction not permitted/i, message: 'Transaction not permitted by your bank' },
    { pattern: /do not honor/i, message: 'Transaction declined by your bank' },
    { pattern: /do not honour?/i, message: 'Transaction declined by your bank' },
    { pattern: /restricted card/i, message: 'Card is restricted for this type of transaction' },
    { pattern: /pickup card/i, message: 'Please contact your bank regarding your card' },
    { pattern: /refer to card issuer/i, message: 'Please contact your bank for assistance' },
    { pattern: /invalid amount/i, message: 'Invalid transaction amount' },
    { pattern: /transaction frequency limit exceeded/i, message: 'Too many transactions attempted. Please try again later' },
    { pattern: /velocity check failed/i, message: 'Transaction limit exceeded. Please try again later' },
]

// System error patterns - errors that are not the customer's fault
const SYSTEM_ERROR_PATTERNS = [
    { pattern: /timeout/i, message: 'Transaction timed out. Please try again' },
    { pattern: /network error/i, message: 'Network error occurred. Please try again' },
    { pattern: /system error/i, message: 'A system error occurred. Please try again' },
    { pattern: /service unavailable/i, message: 'Payment service is temporarily unavailable. Please try again later' },
    { pattern: /connection failed/i, message: 'Connection failed. Please check your internet connection and try again' },
    { pattern: /server error/i, message: 'Server error occurred. Please try again later' },
    { pattern: /processing error/i, message: 'Processing error occurred. Please try again' },
    { pattern: /technical error/i, message: 'Technical error occurred. Please try again later' },
    { pattern: /maintenance/i, message: 'System is under maintenance. Please try again later' },
]

/**
 * Maps a raw error message to a user-friendly message
 * @param {string} rawMessage - The raw error message from the API
 * @returns {object} - Object containing the friendly message and error type
 */
export function mapErrorMessage(rawMessage) {
    if (!rawMessage || typeof rawMessage !== 'string') {
        return {
            message: 'Payment could not be processed. Please try again or contact support if the problem persists.',
            isCustomerError: false,
            originalMessage: rawMessage,
        }
    }

    // Check for customer error patterns first
    for (const pattern of CUSTOMER_ERROR_PATTERNS) {
        if (pattern.pattern.test(rawMessage)) {
            return {
                message: pattern.message,
                isCustomerError: true,
                originalMessage: rawMessage,
            }
        }
    }

    // Check for system error patterns
    for (const pattern of SYSTEM_ERROR_PATTERNS) {
        if (pattern.pattern.test(rawMessage)) {
            return {
                message: pattern.message,
                isCustomerError: false,
                originalMessage: rawMessage,
            }
        }
    }

    // If no pattern matches, return a generic message based on common keywords
    const lowerMessage = rawMessage.toLowerCase()

    // Check if it contains customer-related keywords
    if (
        lowerMessage.includes('declined') ||
        lowerMessage.includes('denied') ||
        lowerMessage.includes('rejected') ||
        lowerMessage.includes('invalid')
    ) {
        return {
            message: `Transaction declined: ${rawMessage}`,
            isCustomerError: true,
            originalMessage: rawMessage,
        }
    }

    // Default to system error for unknown messages
    return {
        message: 'Payment could not be processed. Please try again or contact support if the problem persists.',
        isCustomerError: false,
        originalMessage: rawMessage,
    }
}

/**
 * Gets an appropriate generic message based on error type
 * @param {boolean} isCustomerError - Whether this is a customer error
 * @returns {string} - Appropriate generic message
 */
export function getGenericErrorMessage(isCustomerError) {
    if (isCustomerError) {
        return 'Please check your payment details and try again, or contact your bank if the issue persists.'
    } else {
        return 'A technical issue occurred while processing your payment. Please try again or contact support if the problem persists.'
    }
}
